import bs58 from "bs58";

/**
 * 生成邀请码
 * @param userId 用户ID
 * @param poolId 矿池ID
 * @returns 邀请码字符串
 */
export function generateInviteCode(userId: string | number, poolId: string | number): string {
	try {
		// 将 ID 转换为 Buffer 并进行 base58 编码
		const userIdBuffer = Buffer.from(String(userId));
		const poolIdBuffer = Buffer.from(String(poolId));

		const userIdBase58 = bs58.encode(userIdBuffer);
		const poolIdBase58 = bs58.encode(poolIdBuffer);

		// 使用 'i' 作为分隔符拼接
		return `${userIdBase58}i${poolIdBase58}`;
	} catch (error) {
		console.error("Failed to generate invite code:", error);
		return "";
	}
}

function uint8ArrayToBigInt(arr: Uint8Array): bigint {
	let n = 0n;
	for (const byte of arr) {
		n = (n << 8n) + BigInt(byte);
	}
	return n;
}

/**
 * 解析邀请码
 * @param inviteCode 邀请码字符串
 * @returns { userId: string, poolId: string } 或 null
 */
export function parseInviteCode(inviteCode: string): { userId: string; poolId: string } | null {
	try {
		// 使用 '0' 分割邀请码
		const parts = inviteCode.split("0");
		if (parts.length !== 2) {
			throw new Error("Invalid invite code format");
		}

		const [userIdBase58, poolIdBase58] = parts;

		// 解码 base58，返回 Uint8Array
		const userIdUint8Array = bs58.decode(userIdBase58);
		const poolIdUint8Array = bs58.decode(poolIdBase58);
		// 将 Uint8Array 转换为字符串
		const userId = String(Number(uint8ArrayToBigInt(userIdUint8Array)));
		const poolId = String(Number(uint8ArrayToBigInt(poolIdUint8Array)));

		return { userId, poolId };
	} catch (error) {
		console.error("Failed to parse invite code:", error);
		return null;
	}
}

/**
 * 解析邀请码并返回数字类型的 ID
 * @param inviteCode 邀请码字符串
 * @returns { userId: number, poolId: number } 或 null
 */
export function parseInviteCodeAsNumbers(inviteCode: string): { userId: number; poolId: number } | null {
	const result = parseInviteCode(inviteCode);
	if (!result) return null;

	const userId = Number(result.userId);
	const poolId = Number(result.poolId);

	// 验证转换后的数字是否有效
	if (Number.isNaN(userId) || Number.isNaN(poolId)) {
		console.error("Failed to convert invite code to numbers");
		return null;
	}

	return { userId, poolId };
}
